<script setup lang="ts">
import type { StoreOption } from '@/types/reviewPlatforms'

const props = defineProps<{
  isOpen: boolean
  title: string
  externalIdLabel: string
  externalId: string
  currentStoreId: number | null
  availableStores: StoreOption[]
  isSaving: boolean
}>()

const emit = defineEmits<{
  close: []
  save: [storeId: number]
}>()

const { t } = useI18n()

const selectedStoreId = ref<number | null>(props.currentStoreId)

watch(
  () => props.isOpen,
  (open) => {
    if (open) selectedStoreId.value = props.currentStoreId
  }
)

const storeOptions = computed(() => props.availableStores.map((s) => ({ value: s.id, label: s.name })))

function onSave() {
  if (selectedStoreId.value === null) return
  emit('save', selectedStoreId.value)
}
</script>

<template>
  <BaseModal :is-open="isOpen" :heading-title="title" size="md" @close="emit('close')">
    <div class="flex flex-col gap-4 px-6 py-4">
      <div class="space-y-1">
        <p class="text-xs font-medium text-muted-foreground">{{ externalIdLabel }}</p>
        <p class="font-mono text-sm text-foreground">{{ externalId }}</p>
      </div>

      <div class="space-y-2">
        <label class="text-sm font-medium text-foreground">{{
          t('pages.reviewPlatforms.configuration.shared.assignStore')
        }}</label>
        <ComboboxSelect
          :model-value="selectedStoreId ?? undefined"
          :options="storeOptions"
          value-key="value"
          label-key="label"
          :placeholder="t('pages.reviewPlatforms.configuration.shared.selectStore')"
          @update:model-value="(val) => (selectedStoreId = typeof val === 'number' ? val : null)"
        />
      </div>

      <div class="flex items-center justify-end gap-2 pt-2">
        <Button variant="ghost" size="sm" :disabled="isSaving" @click="emit('close')">
          {{ t('pages.reviewPlatforms.configuration.shared.cancel') }}
        </Button>
        <Button variant="default-primary" size="sm" :disabled="isSaving || selectedStoreId === null" @click="onSave">
          {{ t('pages.reviewPlatforms.configuration.shared.validate') }}
        </Button>
      </div>
    </div>
  </BaseModal>
</template>
